import { ReadonlyVec2, ReadonlyVec3, vec3 } from 'gl-matrix';
import { enqueueSnackbar } from '~/utils/notifications';
import { domainEventBus, StructureSetClosedEvent } from '~/domain/events';
import type { Environment } from '~/engine/data/Environment';
import { targetCrosshairCursor } from '~/engine/data/common/customs/cursors/targetCrosshairCursor';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '~/engine/tool/EventHandlerResult';
import { ModifierKeys } from '~/engine/tool/ModifierKeys';
import type { EnvironmentViews } from '~/engine/view/EnvironmentViews';
import { TextureView } from '~/engine/view/TextureView';
import {
    CrossSectionInput,
    CrossSectionPlane,
    contourNestingOnPlane,
    crossSectionPlanesShareSlice,
    releaseFieldSamplingWorkers,
} from '~/engine/util/algorithms/sdf/cross-section';
import { PlaneView } from '~/engine/view/views/PlaneView';
import {
    CrossSectionOverlayRenderer,
    CrossSectionOverlayState,
    crossSectionPlaneOfView,
} from '~/engine/core/render/renderers/CrossSectionOverlayRenderer';
import { LineDrawGesture } from '../../../actions/contouring-actions/LineDrawAction';
import { MOUSE_BUTTON_LEFT, MOUSE_BUTTON_RIGHT } from '../../../MouseButton';
import { CrosshairInteractionMode, Tool } from '../../../Tool';
import { writeRefusal } from '../editableView';
import { isViewCrossSectionDrawable } from '../obliqueGating';
import { CrossSectionEntry, CrossSectionList } from './CrossSectionList';

const ROTATED_VIEW_MESSAGE =
    'Cross-sections cannot be drawn on a rotated view — ' +
    'reset the view orientation to draw here.';
const LINE_LEFT_ITS_PLANE_MESSAGE =
    'The slice changed while a cross-section was being drawn — the unfinished contour was discarded. Draw each cross-section on a single slice.';
const CONTOUR_INSIDE_ANOTHER_MESSAGE =
    'A contour inside another on the same slice has no effect on the shape — the Cross-Section tool does not cut holes. Undo removes it; to cut a hole, erase it after Apply.';
const CONTOUR_AROUND_ANOTHER_MESSAGE =
    'This contour encloses an earlier one on the same slice, so the earlier one no longer affects the shape — the Cross-Section tool does not cut holes. To cut a hole, erase it after Apply.';

export interface CrossSectionDrawnState {
    canUndo: boolean;
    canRedo: boolean;
    /** True while a contour is drawn or a line is in progress. */
    hasDrawing: boolean;
}

export interface CrossSectionToolCallbacks {
    /** Fired on every change to the drawn set, and once on construction. */
    onCrossSectionsChanged: (state: CrossSectionDrawnState) => void;
    /** Fired when the drawn geometry changes. */
    onShapeChanged: () => void;
}

interface PersistedCrossSectionState {
    structure: RegionOfInterest;
    crossSections: CrossSectionList;
    previewVisible: boolean;
}

const handled = (): EventHandlerResult =>
    new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
const notHandled = (): EventHandlerResult =>
    new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);

/**
 * Stores closed contours exactly as drawn, on the plane of each line's first point, for a preview; the structure is untouched until Apply.
 * Contours are drawn through the patient-aligned views of the armed environment ({@link refusalFor}); a line takes its points from the view it was started in alone.
 */
export class CrossSectionTool extends Tool {
    public readonly toolType = 'cross-section';

    /** Drawn contours handed from one instance to the next on a re-arm. */
    private static persisted: PersistedCrossSectionState | null = null;
    /** Subscribed lazily, since subscribing on import breaks tests that mock the bus. */
    private static handoverRelease: { unsubscribe: () => void } | null = null;

    private static onStructureSetClosed(): void {
        CrossSectionTool.persisted = null;
        releaseFieldSamplingWorkers();
    }

    public readonly structure: RegionOfInterest;
    /** The views of the armed environment, read live: views join and leave while the tool is armed. */
    private readonly views: EnvironmentViews | undefined;
    /** The environment the structure belongs to; a view of any other environment is refused. */
    private readonly environment: Environment | undefined;
    /** Stops following {@link views}; cleared once called. */
    private unsubscribeViews: (() => void) | undefined;
    private readonly callbacks: CrossSectionToolCallbacks;
    private readonly crossSections: CrossSectionList;
    private readonly handover: PersistedCrossSectionState;
    /** While a preview of the current contours shows, the overlay hides them so clipped parts are not drawn back. */
    private previewVisible: boolean;

    private gesture: LineDrawGesture | null = null;
    /** The plane of the line's first point; set exactly while `gesture` is. */
    private linePlane: CrossSectionPlane | null = null;
    private readonly renderers = new Map<
        PlaneView,
        CrossSectionOverlayRenderer
    >();

    public constructor(
        structure: RegionOfInterest,
        views: EnvironmentViews | undefined,
        environment: Environment | undefined,
        callbacks: CrossSectionToolCallbacks
    ) {
        super();
        CrossSectionTool.handoverRelease ??= domainEventBus.subscribe(
            StructureSetClosedEvent.EVENT_TYPE,
            CrossSectionTool.onStructureSetClosed
        );
        this.structure = structure;
        this.views = views;
        this.environment = environment;
        this.callbacks = callbacks;
        // Written here and never on dispose: a successor is constructed before its predecessor is disposed.
        const persisted = CrossSectionTool.persisted;
        const adopt = persisted !== null && persisted.structure === structure;
        this.crossSections = adopt
            ? persisted.crossSections
            : new CrossSectionList();
        this.previewVisible = adopt ? persisted.previewVisible : false;
        this.handover = {
            structure,
            crossSections: this.crossSections,
            previewVisible: this.previewVisible,
        };
        CrossSectionTool.persisted = this.handover;
        this.callbacks.onCrossSectionsChanged(this.drawnState());
        this.unsubscribeViews = views?.subscribe(() =>
            this.releaseOverlaysOfRemovedViews()
        );
    }

    public getInputs(): CrossSectionInput[] {
        return this.crossSections.toInputs();
    }

    /** Refuse the cross-section just drawn and put the set back as it was. */
    public rejectLastDrawing(): boolean {
        return this.publishIf(this.crossSections.rejectLast());
    }

    /** Step back the line in progress, or else one drawing action. */
    public undoDrawing(): boolean {
        if (this.gesture) {
            this.abandonLine();
            return true;
        }
        return this.publishIf(this.crossSections.undo());
    }

    public redoDrawing(): boolean {
        return this.publishIf(this.crossSections.redo());
    }

    public forgetDrawingHistory(): void {
        this.crossSections.clearHistory();
        this.callbacks.onCrossSectionsChanged(this.drawnState());
    }

    /** Changes with every mutation of the drawn set and is shared by every instance holding it. */
    public getDrawnRevision(): number {
        return this.crossSections.revision;
    }

    public setPreviewVisible(visible: boolean): void {
        if (this.previewVisible !== visible) {
            this.rememberPreviewVisible(visible);
            this.renderAll();
        }
    }

    /** The only writer of `previewVisible`, so the flag and the handover slot cannot drift apart. */
    private rememberPreviewVisible(visible: boolean): void {
        this.previewVisible = visible;
        this.handover.previewVisible = visible;
    }

    public clearAllCrossSections(): void {
        this.discardLine();
        // Cleared in place so the handover slot keeps pointing at the live set.
        if (!this.publishIf(this.crossSections.clear())) {
            this.renderAll();
        }
    }

    public override isActionInProgress(): boolean {
        return this.gesture !== null;
    }

    public override isStrokeInProgress(): boolean {
        return this.gesture?.isMouseHeld === true;
    }

    public override getDrawingView(): PlaneView | undefined {
        return this.gesture?.view;
    }

    /** A line bound to `view` is dropped with it; the closed contours stay. */
    public override cancelActionIn(view: PlaneView): EventHandlerResult {
        if (this.getDrawingView() !== view) {
            return notHandled();
        }
        this.abandonLine();
        return handled();
    }

    /**
     * A view of another environment, or with none armed, gets the refusal
     * of {@link writeRefusal}; a view of the armed environment that does not
     * sit on its anatomical plane ({@link isViewCrossSectionDrawable}) the
     * rotated-view reason.
     */
    public override refusalFor(view: PlaneView): string | undefined {
        return writeRefusal(view, this.environment, (own) =>
            isViewCrossSectionDrawable(own) ? undefined : ROTATED_VIEW_MESSAGE
        );
    }

    public override usesButtonDuringAction(button: number): boolean {
        return button === MOUSE_BUTTON_RIGHT && this.gesture !== null;
    }

    public override getCrosshairInteractionMode(
        isDrawingActiveInOtherView: boolean
    ): CrosshairInteractionMode {
        return Tool.contouringCrosshairMode(
            isDrawingActiveInOtherView,
            this.isStrokeInProgress()
        );
    }

    public override onActivate(): EventHandlerResult {
        this.renderAll();
        return handled();
    }

    /** A plus crosshair while contours can be drawn; none on an approved structure. */
    public override get cursor(): string | undefined {
        return this.structure.canModifyContours
            ? targetCrosshairCursor(false)
            : undefined;
    }

    public override onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: TextureView
    ): EventHandlerResult {
        if (!(view instanceof PlaneView)) {
            return notHandled();
        }
        const refusal = this.refusalFor(view);
        if (refusal !== undefined) {
            if (button === MOUSE_BUTTON_LEFT) {
                enqueueSnackbar(refusal, { variant: 'warning' });
            }
            return notHandled();
        }
        const inOtherView = this.gesture !== null && view !== this.gesture.view;
        if (button !== MOUSE_BUTTON_LEFT) {
            // Blocks other buttons' default actions in the other views while a line is drawn.
            return inOtherView ? handled() : notHandled();
        }
        if (this.structure.canModifyContours === false) {
            enqueueSnackbar(
                'Modifying approved structures is not allowed. Unapprove the structure to make changes.',
                { variant: 'warning' }
            );
            return notHandled();
        }
        // A line in progress locks drawing to its own view.
        return inOtherView
            ? handled()
            : this.handleLinePress(view, modifierKeys, coordinates);
    }

    public override onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: TextureView
    ): EventHandlerResult {
        if (
            !(view instanceof PlaneView) ||
            this.refusalFor(view) !== undefined
        ) {
            return notHandled();
        }
        if (!this.gesture || view !== this.gesture.view) {
            return notHandled();
        }
        if (this.lineLeftItsPlane(view)) {
            return this.discardLineThatLeftItsPlane();
        }
        this.gesture.move(coordinates, buttons);
        this.renderAll();
        return handled();
    }

    public override onMouseRelease(
        button: number,
        coordinates: ReadonlyVec2,
        view: TextureView
    ): EventHandlerResult {
        if (button === MOUSE_BUTTON_LEFT) {
            if (!this.gesture) {
                return notHandled();
            }
            if (!(view instanceof PlaneView) || view !== this.gesture.view) {
                this.gesture.endHold();
                return notHandled();
            }
            if (
                this.gesture.line.pointsMm.length >= 3 &&
                this.lineLeftItsPlane(view)
            ) {
                return this.discardLineThatLeftItsPlane();
            }
            if (this.gesture.release(coordinates)) {
                this.finishLineCrossSection();
                return handled();
            }
            return notHandled();
        }
        if (button === MOUSE_BUTTON_RIGHT && this.gesture !== null) {
            if (!this.gesture.line.undoLastSegment()) {
                this.discardLine();
            }
            this.renderAll();
            return handled();
        }
        return notHandled();
    }

    public override onMouseDoubleClick(
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (!this.gesture || view !== this.gesture.view) {
            return notHandled();
        }
        if (this.lineLeftItsPlane(view)) {
            return this.discardLineThatLeftItsPlane();
        }
        const point = view.clientToPatientCoordinates(coordinates);
        this.gesture.line.addPoint(point, 0);
        if (this.gesture.line.pointsMm.length >= 3) {
            this.finishLineCrossSection();
        } else {
            this.renderAll();
        }
        return handled();
    }

    public override onKeyPress(
        key: string,
        _modifierKeys: ModifierKeys,
        _view: TextureView
    ): EventHandlerResult {
        if (key !== 'Escape') {
            return notHandled();
        }
        // Two steps, so one keypress never destroys more than expected: the line first, then every contour.
        if (this.gesture) {
            this.abandonLine();
            return handled();
        }
        if (this.crossSections.size > 0) {
            this.clearAllCrossSections();
            return handled();
        }
        return notHandled();
    }

    public override onViewChanged(
        _coordinates: ReadonlyVec2,
        _view: TextureView
    ): void {
        this.renderAll();
    }

    public override onDeactivate(): EventHandlerResult {
        // Only the line in progress is dropped, so a stray mouse-leave does not destroy drawn work.
        this.discardLine();
        this.renderAll();
        return handled();
    }

    public override dispose(): void {
        this.unsubscribeViews?.();
        this.unsubscribeViews = undefined;
        this.discardLine();
        for (const renderer of this.renderers.values()) {
            renderer.dispose();
        }
        this.renderers.clear();
    }

    private handleLinePress(
        view: PlaneView,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2
    ): EventHandlerResult {
        view.snapToImagePixel();
        if (this.lineLeftItsPlane(view)) {
            return this.discardLineThatLeftItsPlane();
        }
        if (!this.gesture) {
            this.gesture = new LineDrawGesture(
                view,
                modifierKeys,
                coordinates,
                () => this.renderAll()
            );
            this.linePlane = crossSectionPlaneOfView(view);
        } else if (this.gesture.press(modifierKeys, coordinates)) {
            this.finishLineCrossSection();
            return handled();
        }
        this.renderAll();
        return handled();
    }

    /** Commit the line in progress, exactly as drawn, to the plane of its first point. */
    private finishLineCrossSection(): void {
        if (!this.gesture || !this.linePlane) {
            return;
        }
        const polygon = this.gesture.line.pointsMm.map((p) => vec3.clone(p));
        const plane = this.linePlane;
        this.discardLine();
        const existing = this.crossSections.findByPlane(plane);
        if (existing !== null) {
            this.warnOfNestedContours(existing, [polygon]);
            this.crossSections.appendTo(existing.id, [polygon]);
        } else {
            this.crossSections.add(plane, [polygon]);
        }
        this.publishDrawnChange();
    }

    private discardLine(): void {
        this.gesture?.dispose();
        this.gesture = null;
        this.linePlane = null;
    }

    private abandonLine(): void {
        this.discardLine();
        this.renderAll();
    }

    /** True when the line's own view no longer shows the plane the line was started on. */
    private lineLeftItsPlane(view: PlaneView): boolean {
        return (
            this.linePlane !== null &&
            !crossSectionPlanesShareSlice(
                this.linePlane,
                crossSectionPlaneOfView(view)
            )
        );
    }

    /** Its points would lie on more than one slice, so the line is dropped with a warning. */
    private discardLineThatLeftItsPlane(): EventHandlerResult {
        this.discardLine();
        enqueueSnackbar(LINE_LEFT_ITS_PLANE_MESSAGE, { variant: 'warning' });
        this.callbacks.onCrossSectionsChanged(this.drawnState());
        this.renderAll();
        return handled();
    }

    /** Warn once per new contour nested with one already on the plane; nesting adds nothing to the union. */
    private warnOfNestedContours(
        entry: CrossSectionEntry,
        polygonsMm: readonly vec3[][]
    ): void {
        const onPlane: (readonly ReadonlyVec3[])[] = [...entry.polygonsMm];
        for (const polygon of polygonsMm) {
            const nesting = contourNestingOnPlane(
                entry.plane,
                polygon,
                onPlane
            );
            if (nesting !== null) {
                enqueueSnackbar(
                    nesting === 'inside'
                        ? CONTOUR_INSIDE_ANOTHER_MESSAGE
                        : CONTOUR_AROUND_ANOTHER_MESSAGE,
                    { variant: 'warning' }
                );
            }
            onPlane.push(polygon);
        }
    }

    private drawnState(): CrossSectionDrawnState {
        return {
            canUndo: this.crossSections.canUndo || this.gesture !== null,
            canRedo: this.crossSections.canRedo,
            hasDrawing: this.crossSections.size > 0 || this.gesture !== null,
        };
    }

    private publishIf(changed: boolean): boolean {
        if (changed) {
            this.publishDrawnChange();
        }
        return changed;
    }

    /** Every mutation goes through here, so the React state, the preview and the overlays all learn of it. */
    private publishDrawnChange(): void {
        // The preview on screen was built from the previous contours, so the overlay shows them again.
        this.rememberPreviewVisible(false);
        this.callbacks.onCrossSectionsChanged(this.drawnState());
        this.callbacks.onShapeChanged();
        this.renderAll();
    }

    private overlayState(): CrossSectionOverlayState {
        const plane = this.linePlane;
        return {
            contours: this.previewVisible ? [] : this.crossSections.toInputs(),
            line:
                this.gesture && plane !== null
                    ? { ...this.gesture.preview(), plane }
                    : null,
        };
    }

    /** Repaints every view of the armed environment, since `render` clears a canvas before it checks the plane. */
    private renderAll(): void {
        const state = this.overlayState();
        for (const view of this.views?.current ?? []) {
            let renderer = this.renderers.get(view);
            if (!renderer) {
                renderer = new CrossSectionOverlayRenderer(view);
                this.renderers.set(view, renderer);
            }
            renderer.render(state);
        }
    }

    /**
     * Clears and forgets the overlay of every view that left the armed
     * environment, so nothing drawn for it stays on a canvas the view that
     * replaces it takes over. A view that joined is not painted here: it is
     * announced before it is sized and fitted, and is painted by the next
     * repaint.
     */
    private releaseOverlaysOfRemovedViews(): void {
        const current = this.views?.current ?? [];
        for (const [view, renderer] of this.renderers) {
            if (!current.includes(view)) {
                renderer.dispose();
                this.renderers.delete(view);
            }
        }
    }
}
